






















































import { Component, Mixins, Prop, PropSync } from 'vue-property-decorator';
import { ScreenText } from '@/lang/ScreenText';
import DeviceWidthCheckerMixin from '@/mixins/deviceWidthChecker.mixin';
import { CatalogSession, SessionBillingCycle, DiscountUnitsEnum } from '@/Model/sessions/catalogTypes';
import { getInstallmentsWithMakeupOnFinalInstallment } from '@/services/checkout/cart/helpers';
import APP_UTILITIES from '@/utilities/commonFunctions';
import APP_CONST from '@/constants/AppConst';

@Component
export default class SessionPriceTag extends Mixins(DeviceWidthCheckerMixin) {
  @Prop({ type: Object, required: true }) readonly session!: CatalogSession;
  @Prop({ type: Number, default: 1, required: false }) readonly quantity!: number;
  @PropSync('isAutomaticDiscountEnabled', { type: Boolean }) isAutomaticDiscountEnabledSync!: boolean;
  @PropSync('isPaymentPlanFlagEnabled', { type: Boolean }) isPaymentPlanFlagEnabledSync!: boolean;

  readonly screenText = new ScreenText();

  unitDiscount = DiscountUnitsEnum.FixedAmountDescription;

  get isEarlyDiscountApplied(): boolean {
    return this.session.earlyRegistrationDiscount.isApplied;
  }

  get isMultiparticipantApplied(): boolean {
    return this.session.multiParticipantDiscount.isApplied;
  }


  get isRecurringPayment(): boolean {
    return [SessionBillingCycle.Weekly, SessionBillingCycle.Monthly].includes(this.session.sessionBillingCycle);
  }

  get finalDiscountPrice(): string {
    const rawPriceStr = this.getRawPrice(this.session.earlyRegistrationDiscount.netItemDiscount + this.session.multiParticipantDiscount.netItemDiscount, this.unitDiscount);
    return this.isRecurringPayment && !this.isPaymentPlanFlagEnabledSync ? `${rawPriceStr}/${this.getRecurringPaymentUnit()}` : rawPriceStr;
  }

  getRawPrice(discountAmount = 0, discountUnit = DiscountUnitsEnum.FixedAmountDescription): string {
    const priceValue = ((this.isRecurringPayment && !this.isPaymentPlanFlagEnabledSync)
      ? getInstallmentsWithMakeupOnFinalInstallment(this.session.price - discountAmount, this.session.billingCycleCount)[0] * this.quantity
      : this.session.price  * this.quantity
    );


    if (this.isRecurringPayment && !this.isPaymentPlanFlagEnabledSync) {
      return APP_UTILITIES.currencyFmt.format(priceValue);
    }

    if (discountUnit === DiscountUnitsEnum.FixedAmountDescription) {
      const fixedAmountDiscountCalc = priceValue - discountAmount * this.quantity;
      const finalAmountDiscountCalc = fixedAmountDiscountCalc > 0
        ? fixedAmountDiscountCalc
        : 0;
      return APP_UTILITIES.currencyFmt.format(finalAmountDiscountCalc);
    }

    return discountUnit === DiscountUnitsEnum.FixedAmountDescription.toString()
      ? APP_UTILITIES.currencyFmt.format((priceValue - discountAmount) || 0)
      : APP_UTILITIES.currencyFmt.format((priceValue - priceValue * (discountAmount / 100)) || 0);
  }

  get formattedPrice(): string {
    if (this.isPaymentPlanFlagEnabledSync) {
      return `$${this.session.price.toFixed(APP_CONST.SESSION_PRICE_DECIMAL_PRECISION)}`;
    }

    const formattedPrice = this.getRawPrice(0, this.unitDiscount);

    return this.isRecurringPayment && !this.isPaymentPlanFlagEnabledSync
      ? formattedPrice + '/' + this.getRecurringPaymentUnit()
      : formattedPrice;
  }

  getRecurringPaymentUnit(): string {
    return this.session.sessionBillingCycle === SessionBillingCycle.Weekly
      ? this.screenText.getScreenText('PRICE_RECURRING_WEEKLY_ABBREVIATION')
      : this.screenText.getScreenText('PRICE_RECURRING_MONTHLY_ABBREVIATION');
  }

  get formattedPerChildText(): string {
    let price = (this.session.earlyRegistrationDiscount.netItemDiscount + this.session.multiParticipantDiscount.netItemDiscount) > 0
      ? this.session.price - (this.session.earlyRegistrationDiscount.netItemDiscount + this.session.multiParticipantDiscount.netItemDiscount)
      : this.session.price;

    // Set price per child to 0 if it's negative
    if(price < 0) {
      price = 0;
    }
    const sessionPrice = APP_UTILITIES.currencyFmt.format(price);
    const perChildText = this.screenText.getScreenText('CHECKOUT_PER_CHILD');
    return `${sessionPrice} ${perChildText}`;
  }

  get tooltipText(): string {
    let text = '';
    if (this.session.price === 0 || (this.session.earlyRegistrationDiscount.netItemDiscount + this.session.multiParticipantDiscount.netItemDiscount) >= this.session.price) {
      text = this.screenText.getScreenText('PRICE_TOOLTIP_FREE');
    }
    else if (this.isRecurringPayment && !this.isPaymentPlanFlagEnabledSync) {
      text = this.screenText.getScreenText('PRICE_TOOLTIP_RECURRING');
    }
    return text;
  }

  get recurringTotalText(): string {
    const totalAmount = this.session.price - (this.session.earlyRegistrationDiscount.netItemDiscount + this.session.multiParticipantDiscount.netItemDiscount) * this.quantity;
    const formattedTotalAmount = APP_UTILITIES.currencyFmt.format(totalAmount);
    return `${formattedTotalAmount} ${this.screenText.getScreenText('PRICE_RECURRING_TOTAL')}`;
  }

  get recurringPaymentCountText(): string {
    return `${this.session.billingCycleCount} ${this.screenText.getScreenText('PRICE_RECURRING_PAYMENTS')}`;
  }

  get originalPriceFormatted(): string {
    return `${APP_UTILITIES.currencyFmt.format(this.session.price)} ${this.screenText.getScreenText('PRICE_RECURRING_TOTAL')}`;
  }

  get paymentPlansAvailableText(): string {
    return this.screenText.getScreenText('PAYMENT_PLANS_AVAILABLE');
  }
}

